import { IconButton } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { LuBell, LuBellOff, LuBellRing } from 'react-icons/lu';
import { toaster } from '@/shared/ui/chakra/toaster';
import { usePushNotifications, type EnableResult } from '../usePushNotifications';

/** Подсказки после нажатия: читать их нужно с паузами на переход в настройки браузера. */
const HINT_DURATION_MS = 12_000;

/* Колокольчик в шапке включает и выключает уведомления админки. Скрыт, если браузер не умеет
   Web Push или сервер не отдал ключ.

   Перечёркнутый (уведомления запрещены в браузере) тоже нажимается: перепроверяет разрешение и,
   если запрет на месте, подсказывает, где его снять. До 03.10.2026 он был неактивным и ничего не
   перепроверял — диспетчер разрешал уведомления в браузере, а колокольчик оставался перечёркнутым
   до перезагрузки вкладки. */
export function NotificationsToggle() {
  const { t } = useTranslation();
  const { state, enable, disable, recheck } = usePushNotifications();

  if (state === 'unsupported') {
    return null;
  }

  const hint = (result: Exclude<EnableResult, 'on'>) => {
    toaster.create({
      title: t(`notifications.${result}`),
      description: t(`notifications.${result}_how`),
      type: result === 'failed' ? 'error' : 'warning',
      duration: HINT_DURATION_MS,
    });
  };

  if (state === 'denied') {
    return (
      <IconButton
        aria-label={t('notifications.blocked')}
        title={t('notifications.blocked_tooltip')}
        variant="ghost"
        size="sm"
        onClick={() => {
          void recheck().then((now) => {
            if (now === 'denied') hint('blocked');
          });
        }}
      >
        <LuBellOff />
      </IconButton>
    );
  }

  const isOn = state === 'on';
  const label = isOn ? t('notifications.disable') : t('notifications.enable');
  return (
    <IconButton
      aria-label={label}
      title={label}
      variant="ghost"
      size="sm"
      loading={state === 'busy'}
      colorPalette={isOn ? 'teal' : undefined}
      onClick={() => {
        if (isOn) {
          void disable();
          return;
        }
        void enable().then((result) => {
          if (result !== 'on') hint(result);
        });
      }}
    >
      {isOn ? <LuBellRing /> : <LuBell />}
    </IconButton>
  );
}
